// NOTE: Metadata implicitly added to every Statsig event from app/main.
// Statsig events accept strings, hence primitive values are transformed to string representation.
type BooleanString = 'true' | 'false';

export interface BaseMetadata {
  isDesktop: BooleanString;
  language: string;
  domain: string;
  dateMode: 'flexible' | 'selectedDate';
}

// NOTE: Core

export enum CoreEventSource {
  ActivitySelection = 'activitySelection',
  HotelSelection = 'hotelSelection',
  TripOverview = 'tripOverview',
  FlightList = 'flightList',
  FerryList = 'ferryList',
  MonthsSelector = 'monthsSelector',
  TripSummary = 'tripSummary',
  HotelDetails = 'hotelDetails',
}

export interface CoreEventMetadata {
  source: CoreEventSource;
}

// NOTE: Trip overview

export interface TripOverviewBookClickedMetadata {
  totalIngredients: number;
  totalAccomodation: number;
  totalOwnStay: number;
  totalFlight: number;
  totalFerry: number;
  totalActivity: number;
  totalAmount: number;
  market: string;
}

export interface TripOverviewInitiatedMetadata {
  totalAccomodation: number;
  totalFlight: number;
  totalFerry: number;
  totalActivity: number;
}

export enum EditTripModalOpenedLocationType {
  TripOverviewAddDestinationButton = 'TripOverviewAddDestinationButton',
  TripOverviewEditTripButton = 'TripOverviewEditTripButton',
  TripOverviewDestinationSegment = 'TripOverviewDestinationSegment',
  TripOverviewAddDestinationToOwnStayButton = 'TripOverviewAddDestinationToOwnStayButton',
}

export interface TripOverviewUnavailableProductActionClickedMetadata
  extends Record<string, string> {
  actionType: 'changeFilters' | 'changeProduct' | 'changeDate';
}

export interface TripOverviewUnavailableProductRenderedMetadata
  extends Record<string, string> {
  productType: 'accommodation' | 'activity' | 'flight' | 'ferry';
  isSelectedProduct: BooleanString;
  hasSelectedFilters: BooleanString;
}

export interface TripOverviewHeaderActionsMetadata
  extends Record<string, string> {
  actionType:
    | 'editTrip'
    | 'paxSelector'
    | 'monthsSelector'
    | 'homeAirport'
    | 'editStay';
}

export interface TripOverviewProductTourStepMetadata
  extends Record<string, string> {
  detail: 'change_hotel' | 'nights' | 'pax' | 'calendar' | 'transport';
  step: '1' | '2' | '3' | '4' | '5';
}

export interface TripOverviewPreferencesModalMetadata
  extends Record<string, string> {
  source: 'from-plus' | 'from-more';
}

// NOTE: Hotel details

export enum HotelDetailsEventSource {
  HotelSelection = 'hotelSelection',
  HotelDetailsSelectionBox = 'hotelDetailsSelectionBox',
  TripOverview = 'tripOverview',
  TripOverviewAlternativeHotels = 'tripOverviewAlternativeHotels',
  StandaloneView = 'standaloneView',
}

export interface HotelDetailsEventMetadata {
  source: HotelDetailsEventSource;
}

// NOTE: Change hotel
export enum ChangeHotelEventSource {
  AvailableAccommodation = 'availableAccommodation',
  AlternativeAccommodation = 'alternativeAccommodation',
  UnavailableAccommodation = 'unavailableAccommodation',
}

export interface ChangeHotelEventMetadata {
  source: ChangeHotelEventSource;
}

// NOTE: Hotel selection
export enum CloseHotelSelectionFiltersOrigin {
  ShowResultsButton = 'show results button',
  CloseButton = 'close button',
}

export enum RemoveHotelSelectionFiltersOrigin {
  FilterList = 'filter list',
  HotelList = 'hotel list',
  TripOverview = 'trip overview',
}

export interface HotelSelectionInitiatedMetadata {
  placeId: number;
  placeName: string;
  hotelResults: number;
}

export interface HotelSelectionFilterOpenedMetadata {
  polygonId: number;
  label: string;
  hotelResults: number;
}

export interface HotelSelectionFilterClosedMetadata {
  polygonId: number;
  label: string;
  hotelResults: number;
  eventPlacement: CloseHotelSelectionFiltersOrigin;
}

export interface HotelSelectionFilterAppliedMetadata {
  filterName: string;
  filterGroup: string;
  filterDetail: string;
  polygonId: number;
  pathname: string;
}

export interface HotelSelectionFilterRemovedMetadata {
  filterName: string;
  filterGroup: string;
  filterDetail: string;
  polygonId: number;
  pathname: string;
  eventPlacement: RemoveHotelSelectionFiltersOrigin;
}

export interface HotelSelectionFilterResetMetadata {
  polygonId: number;
  label: string;
}

export interface ActivityDetailsTicketSelectedMetadata {
  productCode: string;
}

export interface StandaloneHotelSearchMetadata {
  type: 'name' | 'code';
}
export interface TripOverviewPrintModeMetadata {
  source: 'button' | 'shortcut';
}
